/**
 * Token Refresh Manager
 * Manages token refresh state and subscriber queues per client
 * Fixes memory leaks and race conditions
 */

import { MAX_REFRESH_SUBSCRIBERS } from "./constants";

interface RefreshSubscriber {
  onSuccess: (token: string) => void;
  onFailure: (error: Error) => void;
}

/**
 * Manages token refresh state per client to prevent race conditions
 */
export class TokenRefreshManager {
  private refreshState = new Map<string, boolean>();
  private subscribers = new Map<string, RefreshSubscriber[]>();

  isRefreshing(clientId: string): boolean {
    return this.refreshState.get(clientId) ?? false;
  }

  setRefreshing(clientId: string, value: boolean): void {
    this.refreshState.set(clientId, value);
  }

  subscribe(clientId: string, subscriber: RefreshSubscriber): void {
    const clientSubscribers = this.subscribers.get(clientId) ?? [];

    // Prevent memory leak: limit subscribers
    if (clientSubscribers.length >= MAX_REFRESH_SUBSCRIBERS) {
      console.warn(
        `[API] Too many queued requests for client "${clientId}" (${clientSubscribers.length}), clearing oldest subscribers`,
      );
      // Keep the most recent subscribers
      clientSubscribers.splice(
        0,
        clientSubscribers.length - MAX_REFRESH_SUBSCRIBERS + 1,
      );
    }

    clientSubscribers.push(subscriber);
    this.subscribers.set(clientId, clientSubscribers);
  }

  /**
   * Notify all subscribers of successful token refresh
   */
  notifySuccess(clientId: string, newToken: string): void {
    const clientSubscribers = this.subscribers.get(clientId) ?? [];

    clientSubscribers.forEach((subscriber) => {
      try {
        subscriber.onSuccess(newToken);
      } catch (error) {
        console.error("[API] Error in refresh subscriber (success):", error);
      }
    });

    this.subscribers.delete(clientId);
    this.setRefreshing(clientId, false);
  }

  /**
   * Notify all subscribers of failed token refresh
   */
  notifyFailure(clientId: string, error: Error): void {
    const clientSubscribers = this.subscribers.get(clientId) ?? [];

    clientSubscribers.forEach((subscriber) => {
      try {
        subscriber.onFailure(error);
      } catch (err) {
        console.error("[API] Error in refresh subscriber (failure):", err);
      }
    });

    this.subscribers.delete(clientId);
    this.setRefreshing(clientId, false);
  }

  /**
   * Get subscriber count for a client (for metrics)
   */
  getSubscriberCount(clientId: string): number {
    return this.subscribers.get(clientId)?.length ?? 0;
  }

  /**
   * Clear all state for a client
   */
  clear(clientId: string): void {
    this.refreshState.delete(clientId);
    this.subscribers.delete(clientId);
  }

  /**
   * Clear all state (for testing/reset)
   */
  reset(): void {
    this.refreshState.clear();
    this.subscribers.clear();
  }
}

/**
 * Global token refresh manager instance
 */
let globalTokenRefreshManager: TokenRefreshManager | null = null;

/**
 * Get the global token refresh manager
 */
export function getTokenRefreshManager(): TokenRefreshManager {
  if (!globalTokenRefreshManager) {
    globalTokenRefreshManager = new TokenRefreshManager();
  }
  return globalTokenRefreshManager;
}

/**
 * Reset the global token refresh manager (for testing)
 */
export function resetTokenRefreshManager(): void {
  if (globalTokenRefreshManager) {
    globalTokenRefreshManager.reset();
  }
}
